import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RequestRow {
  readonly externalRequestId: string;
  readonly requestCode: string;
  readonly subject: string;
  readonly requestType: string;
  readonly priority: string;
  readonly status: string;
}

const REQUEST_TYPES = ['SUPPORT', 'CHANGE', 'CAMPAIGN', 'ASSET', 'APPROVAL', 'ISSUE', 'ESCALATION', 'FEEDBACK'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const STATUSES = ['DRAFT', 'SUBMITTED', 'IN_REVIEW', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'CLOSED', 'REJECTED', 'CANCELLED'];
const PAGE = 25;

/**
 * M42 §27 RequestManagementScreen (§13/§14). Stakeholders raise, track and
 * submit external service requests. Viewing needs M42_REQUEST_VIEW, drafting
 * needs M42_REQUEST_MANAGE, submitting needs M42_REQUEST_SUBMIT.
 */
export function RequestManagementScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [portalUserId, setPortalUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [requestType, setRequestType] = useState('SUPPORT');
  const [priority, setPriority] = useState('MEDIUM');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: RequestRow[]; total: number }>(`/v1/portal/requests?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, page, reload]);

  const raise = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!subject.trim()) { setActionError(t('m42.request.subjectRequired')); return; }
    try {
      await apiRequest('/v1/portal/requests', {
        method: 'POST',
        body: { portalUserId: portalUserId.trim() || null, subject: subject.trim(), requestType, priority },
        token,
      });
      setNotice(t('m42.request.drafted'));
      setSubject('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.request.notAuthRaise'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const submit = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/portal/requests/${id}/submit`, { method: 'POST', body: {}, token });
      setNotice(t('m42.request.submitted'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.request.notAuthSubmit'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="request-management">
      <h1>{t('m42.request.title')}</h1>

      <section aria-label="request-create">
        <h2>{t('m42.request.raiseHeading')}</h2>
        <form aria-label="request-form" onSubmit={(e) => void raise(e)}>
          <RecordPicker<{ portalUserId: string; displayName?: string; email?: string }>
            endpoint="/v1/portal/users?limit=200"
            idField="portalUserId"
            label={t('m42.common.portalUser')}
            testId="field-portal-user"
            value={portalUserId}
            optionLabel={(u) => u.displayName ?? u.email ?? u.portalUserId}
            onPick={setPortalUserId}
            emptyText="No portal user on file"
          />
          <label>{t('m42.common.subject')}<input aria-label="field-request-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
          <label>{t('m42.common.type')}
            <select aria-label="field-request-type" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
              {REQUEST_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m42.common.priority')}
            <select aria-label="field-request-priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-request">{t('m42.request.draftBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="request-action-error">{actionError}</p>}
        {notice && <p aria-label="request-notice">{notice}</p>}
      </section>

      <section aria-label="request-filters">
        <label>{t('m42.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m42.common.all')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="request-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="request-total">{t('m42.request.total', { count: total })}</p>
          <DataTable
            ariaLabel="request-table"
            rows={rows}
            rowKey={(r) => r.externalRequestId}
            emptyMessage={t('m42.request.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (r) => `request-code-${r.externalRequestId}`, render: (r) => r.requestCode },
              { key: 'subject', header: t('m42.common.subject'), render: (r) => r.subject },
              { key: 'type', header: t('m42.common.type'), render: (r) => r.requestType },
              { key: 'priority', header: t('m42.common.priority'), render: (r) => r.priority },
              { key: 'status', header: t('m42.common.status'), cellAriaLabel: (r) => `request-status-${r.externalRequestId}`, render: (r) => r.status },
              { key: 'actions', header: t('m42.common.actions'), render: (r) => (
                <Button variant="secondary" type="button" aria-label={`submit-${r.externalRequestId}`} onClick={() => void submit(r.externalRequestId)}>{t('m42.request.submitBtn')}</Button>
              ) },
            ]}
          />

          <nav aria-label="request-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
